import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import useAuth from 'hooks/useAuth'

// SSE Event Types
export type SSEEventType =
  | 'heartbeat'
  | 'bulk_pdf_ready'
  | 'bulk_pdf_error'
  | 'bulk_pdf_progress'
  | 'bulk_email_ready'
  | 'bulk_email_error'
  | 'bulk_email_progress'
  | 'agent_categories_prepared'
  | 'agent_rewards_prepared'
  | 'agent_error'
  | 'agent_processing'
  | 'agent.started'
  | 'agent.completed'
  | 'agent.failed'
  | 'tool.execution_started'
  | 'tool.execution_completed'
  | 'tool.execution_failed'
  | 'model.response_started'
  | 'model.response_completed'
  | 'model.response_failed'

// Connection States
export type SSEConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

// Event Structure
export interface SSEEvent {
  type: SSEEventType
  data?: Record<string, any>
  timestamp: string
}

// Event Handler Type
export type SSEEventHandler = (event: SSEEvent) => void

interface SSEContextValue {
  connectionState: SSEConnectionState
  subscribe: (eventType: SSEEventType, handler: SSEEventHandler) => () => void
  isConnected: boolean
}

const SSEContext = createContext<SSEContextValue>({
  connectionState: 'disconnected',
  subscribe: () => () => {},
  isConnected: false,
})

interface SSEProviderProps {
  children: React.ReactNode
}

const MAX_BACKOFF = 16000 // 16 seconds
const HEARTBEAT_TIMEOUT = 35000 // 35 seconds (backend sends every 30s)
const INITIAL_BACKOFF = 1000 // 1 second

export const SSEProvider: React.FC<SSEProviderProps> = ({ children }) => {
  const { token, profile } = useAuth()
  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected')

  // Refs to persist across renders
  const abortControllerRef = useRef<AbortController | null>(null)
  const subscribersRef = useRef<Map<SSEEventType, Set<SSEEventHandler>>>(new Map())
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef<number>(0)
  const isConnectingRef = useRef<boolean>(false)

  const userId = profile?.user?.id

  // Clear timers helper
  const clearTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearTimeout(heartbeatTimerRef.current)
      heartbeatTimerRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  // Reset heartbeat timer
  const resetHeartbeatTimer = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearTimeout(heartbeatTimerRef.current)
    }

    heartbeatTimerRef.current = setTimeout(() => {
      console.warn('[SSE] Heartbeat timeout - connection may be dead')
      // Attempt to reconnect
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      setConnectionState('reconnecting')
      reconnectSSE()
    }, HEARTBEAT_TIMEOUT)
  }, [])

  // Calculate backoff delay with exponential backoff
  const getBackoffDelay = useCallback(() => {
    const delay = Math.min(INITIAL_BACKOFF * Math.pow(2, reconnectAttemptsRef.current), MAX_BACKOFF)
    return delay
  }, [])

  // Parse SSE message
  const parseSSEMessage = useCallback((rawData: string): { event: string; data: string } | null => {
    const lines = rawData.split('\n')
    let event = 'message'
    let data = ''

    for (const line of lines) {
      if (line.startsWith('event:')) {
        event = line.substring(6).trim()
      } else if (line.startsWith('data:')) {
        data = line.substring(5).trim()
      }
    }

    if (data) {
      return { event, data }
    }
    return null
  }, [])

  // Connect to SSE endpoint using fetch
  const connectSSE = useCallback(() => {
    if (!token || !userId || isConnectingRef.current) {
      return
    }

    isConnectingRef.current = true
    setConnectionState('connecting')

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const url = `${process.env.REACT_APP_API_SERVER_URL}sse/notifications/${userId}`

    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        if (!response.body) {
          throw new Error('Response body is null')
        }

        console.log('[SSE] Connection established')
        setConnectionState('connected')
        isConnectingRef.current = false
        reconnectAttemptsRef.current = 0
        resetHeartbeatTimer()

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            console.log('[SSE] Stream ended')
            break
          }

          // Decode the chunk and add to buffer
          buffer += decoder.decode(value, { stream: true })

          // Split by double newlines (SSE message separator)
          const messages = buffer.split('\n\n')

          // Keep the last incomplete message in the buffer
          buffer = messages.pop() || ''

          // Process complete messages
          for (const rawMessage of messages) {
            if (rawMessage.trim()) {
              const parsed = parseSSEMessage(rawMessage)
              if (parsed) {
                const { event, data } = parsed

                try {
                  const parsedData = JSON.parse(data) as SSEEvent

                  // Reset heartbeat timer on any message
                  resetHeartbeatTimer()

                  // Notify subscribers
                  const eventType = event as SSEEventType
                  const handlers = subscribersRef.current.get(eventType)
                  if (handlers) {
                    handlers.forEach((handler) => {
                      try {
                        handler(parsedData)
                      } catch (err) {
                        console.error(`[SSE] Error in handler for ${eventType}:`, err)
                      }
                    })
                  }
                } catch (err) {
                  console.error('[SSE] Failed to parse event data:', err)
                }
              }
            }
          }
        }

        // Stream ended, reconnect
        isConnectingRef.current = false
        setConnectionState('reconnecting')
        reconnectSSE()
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('[SSE] Connection aborted')
        } else {
          console.error('[SSE] Connection error:', error)
        }
        isConnectingRef.current = false
        setConnectionState('reconnecting')
        reconnectSSE()
      })
  }, [token, userId, resetHeartbeatTimer, parseSSEMessage])

  // Reconnect with exponential backoff
  const reconnectSSE = useCallback(() => {
    clearTimers()

    const delay = getBackoffDelay()
    console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`)

    reconnectTimerRef.current = setTimeout(() => {
      reconnectAttemptsRef.current += 1
      connectSSE()
    }, delay)
  }, [clearTimers, getBackoffDelay, connectSSE])

  // Subscribe to an event type
  const subscribe = useCallback((eventType: SSEEventType, handler: SSEEventHandler) => {
    if (!subscribersRef.current.has(eventType)) {
      subscribersRef.current.set(eventType, new Set())
    }
    subscribersRef.current.get(eventType)!.add(handler)

    // Return unsubscribe function
    return () => {
      const handlers = subscribersRef.current.get(eventType)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          subscribersRef.current.delete(eventType)
        }
      }
    }
  }, [])

  // Connect on mount and when userId/token changes
  useEffect(() => {
    if (userId && token) {
      connectSSE()
    }

    // Cleanup on unmount
    return () => {
      clearTimers()
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      isConnectingRef.current = false
    }
  }, [userId, token, connectSSE, clearTimers])

  const value: SSEContextValue = {
    connectionState,
    subscribe,
    isConnected: connectionState === 'connected',
  }

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>
}

// Custom hook to use SSE context
export const useSSE = () => {
  const context = useContext(SSEContext)
  if (!context) {
    throw new Error('useSSE must be used within SSEProvider')
  }
  return context
}

export default SSEContext
